import { Link, type ErrorComponentProps } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { cn } from '@aitoken/ui/lib/utils';
import { ErrorAlert } from '@/components/error-state';
import { Wordmark } from '@/components/wordmark';
import { usePublicT } from '@/i18n/messages/public';

/** embedded：在公共布局里（如不存在的文档页）使用时，不再画字标、不再包一层 main。 */
export function NotFoundPage() {
  return <NotFoundView />;
}

export function NotFoundView({ embedded = false }: { embedded?: boolean }) {
  const t = usePublicT();
  const Root = embedded ? 'div' : 'main';
  return (
    <Root
      id={embedded ? undefined : 'main'}
      className={cn(
        'dark field-ink flex flex-col justify-between gap-12 bg-ink p-[var(--px)] text-white',
        embedded ? 'min-h-[70svh] py-20' : 'min-h-svh',
      )}
    >
      {embedded ? null : (
        <Link
          to="/"
          aria-label={t('notFound.backHome')}
          className="w-fit rounded-[4px] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold"
        >
          <Wordmark />
        </Link>
      )}
      <div className="flex flex-col gap-8">
        <p className="display text-[clamp(120px,22vw,320px)] leading-[0.8] text-gold" aria-hidden>
          404
        </p>
        <h1 className="display-cjk text-[clamp(36px,4.4vw,64px)]">{t('notFound.title')}</h1>
        <p className="step-body max-w-[30em] text-[#e4e2de]">{t('notFound.body')}</p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/"
            className="press inline-flex h-12 items-center gap-2 rounded-[4px] bg-gold px-6 text-base font-semibold text-ink hover:bg-[#dcc69a] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            {t('notFound.backHome')}
            <ArrowRight className="size-4" aria-hidden />
          </Link>
          <Link
            to="/docs"
            className="press inline-flex h-12 items-center rounded-[4px] bg-white/10 px-6 text-base font-semibold text-white hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            {t('notFound.docs')}
          </Link>
        </div>
      </div>
      <span aria-hidden />
    </Root>
  );
}

export function RouteErrorPage({ error, reset }: ErrorComponentProps) {
  const t = usePublicT();
  return (
    <div className="mx-auto w-full max-w-xl p-6">
      <ErrorAlert error={error} title={t('routeError.title')} onRetry={reset} />
    </div>
  );
}
